import React, { Fragment, useEffect, useMemo, useState } from "react";

// Shareable decision report. Opened from the app's Home tab via a signed,
// one-time link: the data endpoint deletes the link as soon as it's read, so a
// refresh can't load it again (the page says so).

const DAY = 24 * 60 * 60 * 1000;

const toTime = (value) => {
  if (!value) return null;
  const t = new Date(typeof value === 'number' && value < 1e12 ? value * 1000 : value).getTime();
  return Number.isNaN(t) ? null : t;
};

const formatDate = (value, withTime) => {
  const t = toTime(value);
  if (t === null) return '';
  const opts = { day: 'numeric', month: 'short', year: 'numeric' };
  if (withTime) Object.assign(opts, { hour: 'numeric', minute: '2-digit' });
  return new Date(t).toLocaleString(undefined, opts);
};

const relative = (value) => {
  const t = toTime(value);
  if (t === null) return '';
  const days = Math.round((t - Date.now()) / DAY);
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  return days > 0 ? `in ${days} days` : `${-days} days ago`;
};

const initials = (name) => (name || '?')
  .split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join('') || '?';

// Stable pastel per person so the same name always gets the same colour.
const TONES = ['blush', 'sky', 'mint', 'butter', 'lilac'];
const toneFor = (key) => {
  let h = 0;
  for (const ch of String(key || '')) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return TONES[h % TONES.length];
};

// Slack message text uses <@U123>, <#C123|name> and <url|label> markup.
const slackText = (text, people) => text
  .replace(/<@([A-Z0-9]+)(?:\|([^>]+))?>/g, (m, id, label) => `@${label || (people.get(id) || {}).name || 'someone'}`)
  .replace(/<#[A-Z0-9]+\|([^>]+)>/g, '#$1')
  .replace(/<(https?:[^|>]+)\|([^>]+)>/g, '$2')
  .replace(/<(https?:[^>]+)>/g, '$1')
  .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');

const statusOf = (d) => {
  if (d.decided_at) return 'decided';
  const deadline = toTime(d.deadline_at);
  if (deadline !== null && deadline < Date.now()) return 'overdue';
  return 'open';
};

const STATUS_LABEL = { decided: 'Decided', open: 'In progress', overdue: 'Overdue' };

// The server only sends a member the decisions they're part of; admins get the
// whole workspace, so only they see an "Everyone's" view.
const scopesFor = (isAdmin) => [
  ['stakeholder', 'You’re a stakeholder'],
  ['created', 'You created'],
  ['all', isAdmin ? 'Everyone’s' : 'All yours'],
];

const Avatar = ({ person, size }) => (
  <span className={`rp-avatar rp-avatar--${size || 'md'} dt-tone--${toneFor(person && (person.id || person.name))}`} title={person && person.name}>
    {initials(person && person.name)}
  </span>
);

const Report = () => {
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState('stakeholder');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [outcome, setOutcome] = useState('all');
  const [sortKey, setSortKey] = useState('created_at');
  const [ascending, setAscending] = useState(false);

  const query = useMemo(() => new URLSearchParams(window.location.search), []);
  const token = query.get('token');
  const team = query.get('team');
  const sig = query.get('sig');

  useEffect(() => {
    document.title = 'Decision report · Decision Tracker';
    let alive = true;
    (async () => {
      if (!token || !team || !sig) {
        setError('This link is missing some details. Open a new report from the Decision Tracker Home tab in Slack.');
        setLoading(false);
        return;
      }
      try {
        const res = await fetch(`/api/report/data?token=${encodeURIComponent(token)}&team=${encodeURIComponent(team)}&sig=${encodeURIComponent(sig)}`);
        if (!res.ok) {
          throw new Error(res.status === 403
            ? 'This report link has already been used or has expired. Links work once and last an hour. Open a new report from the Decision Tracker Home tab in Slack.'
            : 'The report couldn’t be loaded. Try opening a new report from the Decision Tracker Home tab in Slack.');
        }
        const payload = await res.json();
        if (alive) setReport(payload);
      } catch (err) {
        if (alive) setError(err.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token, team, sig]);

  const decisions = (report && report.decisions) || [];
  const hasViewer = Boolean(report && report.viewer);

  const people = useMemo(() => {
    const map = new Map();
    decisions.forEach((d) => {
      [d.created_by, d.decided_by, ...(d.stakeholders || [])].forEach((p) => { if (p && p.id) map.set(p.id, p); });
    });
    return map;
  }, [decisions]);

  const scopeCounts = useMemo(() => ({
    stakeholder: decisions.filter((d) => d.viewer && d.viewer.is_stakeholder).length,
    created: decisions.filter((d) => d.viewer && d.viewer.is_creator).length,
    all: decisions.length,
  }), [decisions]);

  const effectiveScope = hasViewer ? scope : 'all';
  const inScope = useMemo(() => decisions.filter((d) => {
    if (effectiveScope === 'stakeholder') return d.viewer && d.viewer.is_stakeholder;
    if (effectiveScope === 'created') return d.viewer && d.viewer.is_creator;
    return true;
  }), [decisions, effectiveScope]);

  const stats = useMemo(() => ({
    total: inScope.length,
    needsYou: inScope.filter((d) => d.viewer && d.viewer.approval_status === 'Pending' && !d.decided_at).length,
    open: inScope.filter((d) => statusOf(d) !== 'decided').length,
    overdue: inScope.filter((d) => statusOf(d) === 'overdue').length,
    decided: inScope.filter((d) => statusOf(d) === 'decided').length,
  }), [inScope]);

  const outcomeOptions = useMemo(() => {
    const set = new Set(decisions.map((d) => d.outcome_status).filter(Boolean));
    return Array.from(set);
  }, [decisions]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = inScope;
    if (status !== 'all') list = list.filter((d) => (status === 'open' ? statusOf(d) !== 'decided' : statusOf(d) === status));
    if (outcome !== 'all') list = list.filter((d) => (outcome === 'none' ? !d.outcome_status : d.outcome_status === outcome));
    if (q) {
      list = list.filter((d) => [
        d.title, d.description, d.expected_outcome, d.actual_outcome, d.outcome_notes,
        d.created_by && d.created_by.name, d.decided_by && d.decided_by.name,
        ...(d.stakeholders || []).map((s) => s.name),
      ].filter(Boolean).join(' ').toLowerCase().includes(q));
    }
    const value = (d) => {
      if (sortKey === 'title') return (d.title || '').toLowerCase();
      if (sortKey === 'status') return ['overdue', 'open', 'decided'].indexOf(statusOf(d));
      const t = toTime(d[sortKey]);
      return t === null ? (ascending ? Infinity : -Infinity) : t;
    };
    return [...list].sort((a, b) => {
      const va = value(a); const vb = value(b);
      if (va === vb) return 0;
      return (va > vb ? 1 : -1) * (ascending ? 1 : -1);
    });
  }, [inScope, search, status, outcome, sortKey, ascending]);

  const filtersActive = search || status !== 'all' || outcome !== 'all';
  const clearFilters = () => { setSearch(''); setStatus('all'); setOutcome('all'); };

  const viewerName = hasViewer && report.viewer.name && report.viewer.name !== 'Unknown' ? report.viewer.name : null;
  const firstName = viewerName ? viewerName.split(' ')[0] : null;

  return (
    <div className="dt rp">
      <header className="rp-bar">
        <div className="rp-wrap rp-bar-inner">
          <a href="/" className="dt-brand">
            <img src="/images/logo.png" alt="" />
            Decision Tracker
          </a>
          {report && (
            <button type="button" className="dt-btn dt-btn-quiet dt-btn-sm rp-print" onClick={() => window.print()}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 9V4h10v5" /><rect x="4" y="9" width="16" height="8" rx="2" /><path d="M7 14h10v6H7z" /></svg>
              Save as PDF
            </button>
          )}
        </div>
      </header>

      <main className="rp-wrap rp-main">
        {loading && (
          <div className="rp-skeleton" aria-live="polite" aria-label="Loading report">
            <div className="rp-sk rp-sk--title" />
            <div className="rp-sk-row">{[0, 1, 2, 3].map((i) => <div key={i} className="rp-sk rp-sk--tile" />)}</div>
            {[0, 1, 2].map((i) => <div key={i} className="rp-sk rp-sk--card" />)}
          </div>
        )}

        {!loading && error && (
          <div className="rp-empty rp-empty--error">
            <div className="rp-empty-art" aria-hidden="true">🔒</div>
            <h1 className="dt-h2">This report isn’t available</h1>
            <p className="dt-body">{error}</p>
          </div>
        )}

        {!loading && report && (
          <Fragment>
            <section className="rp-head">
              <p className="rp-eyebrow">{(report.team && report.team.name) || team} · {formatDate(report.generated_at, true)}</p>
              <h1 className="dt-page-title">
                {firstName ? <Fragment>{firstName}’s decisions</Fragment> : (hasViewer ? 'Your decisions' : 'Decision report')}
              </h1>
              <p className="rp-note">This link works once, so keep this tab open or save it as a PDF.</p>

              {hasViewer && (
                <div className="rp-scope" role="tablist" aria-label="Which decisions">
                  {scopesFor(report.viewer.is_admin).map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      role="tab"
                      aria-selected={scope === key}
                      className={`rp-scope-btn${scope === key ? ' is-active' : ''}`}
                      onClick={() => setScope(key)}
                    >
                      {label}<span className="rp-count">{scopeCounts[key]}</span>
                    </button>
                  ))}
                </div>
              )}
            </section>

            <section className="rp-stats" aria-label="Summary">
              {[
                ['blush', stats.needsYou, 'Need your approval', 'needs'],
                ['butter', stats.overdue, 'Overdue', 'overdue'],
                ['sky', stats.open, 'Still open', 'open'],
                ['mint', stats.decided, 'Decided', 'decided'],
              ].filter(([, , , key]) => key !== 'needs' || hasViewer).map(([tone, value, label, key]) => (
                <button
                  type="button"
                  key={key}
                  className={`rp-stat dt-tone--${tone}${status === key ? ' is-active' : ''}`}
                  onClick={() => setStatus(key === 'needs' ? 'open' : (status === key ? 'all' : key))}
                >
                  <span className="rp-stat-value">{value}</span>
                  <span className="rp-stat-label">{label}</span>
                </button>
              ))}
            </section>

            <section className="rp-toolbar" aria-label="Filter decisions">
              <label className="rp-search">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6" /><path d="M15 15l5 5" /></svg>
                <input type="search" placeholder="Search" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search decisions" />
              </label>
              <div className="rp-chips" role="group" aria-label="Status">
                {[['all', 'All'], ['open', 'Open'], ['overdue', 'Overdue'], ['decided', 'Decided']].map(([key, label]) => (
                  <button key={key} type="button" className={`rp-chip${status === key ? ' is-active' : ''}`} onClick={() => setStatus(key)}>{label}</button>
                ))}
              </div>
              <div className="rp-selects">
                {outcomeOptions.length > 0 && (
                  <select value={outcome} onChange={(e) => setOutcome(e.target.value)} aria-label="Outcome">
                    <option value="all">Any outcome</option>
                    {outcomeOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                    <option value="none">No outcome yet</option>
                  </select>
                )}
                <select value={sortKey} onChange={(e) => setSortKey(e.target.value)} aria-label="Sort by">
                  <option value="created_at">Created date</option>
                  <option value="deadline_at">Deadline</option>
                  <option value="decided_at">Decided date</option>
                  <option value="status">Status</option>
                  <option value="title">Title</option>
                </select>
                <button type="button" className="rp-order" onClick={() => setAscending(!ascending)} aria-label={ascending ? 'Ascending' : 'Descending'} title={ascending ? 'Ascending' : 'Descending'}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" style={{ transform: ascending ? 'scaleY(-1)' : 'none' }} aria-hidden="true"><path d="M12 5v14" /><path d="M6 13l6 6 6-6" /></svg>
                </button>
              </div>
            </section>

            <p className="rp-showing">
              Showing {shown.length} of {inScope.length}
              {filtersActive && <button type="button" className="rp-link" onClick={clearFilters}>Clear filters</button>}
            </p>

            {shown.length === 0 ? (
              <div className="rp-empty">
                <div className="rp-empty-art" aria-hidden="true">{effectiveScope === 'stakeholder' && !filtersActive ? '🌤️' : '🔍'}</div>
                {effectiveScope === 'stakeholder' && !filtersActive ? (
                  <Fragment>
                    <h2 className="dt-h3">You’re not a stakeholder on any decisions yet</h2>
                    <p className="dt-body">When someone adds you to a decision, it shows up here.</p>
                    {scopeCounts.all > 0 && (
                      <button type="button" className="dt-btn dt-btn-quiet" onClick={() => setScope('all')}>
                        {report.viewer.is_admin ? 'See everyone’s decisions' : 'See all your decisions'}
                      </button>
                    )}
                  </Fragment>
                ) : (
                  <Fragment>
                    <h2 className="dt-h3">No decisions match</h2>
                    {filtersActive && <button type="button" className="dt-btn dt-btn-quiet" onClick={clearFilters}>Clear filters</button>}
                  </Fragment>
                )}
              </div>
            ) : (
              <ol className="rp-list">
                {shown.map((d, i) => <DecisionCard key={d.id || i} d={d} people={people} />)}
              </ol>
            )}
          </Fragment>
        )}
      </main>
    </div>
  );
};

const DecisionCard = ({ d, people }) => {
  const st = statusOf(d);
  const you = d.viewer || {};
  const convos = d.linked_conversations || [];
  const approved = (d.stakeholders || []).filter((s) => s.status === 'Approved').length;

  return (
    <li className={`rp-card rp-card--${st}`}>
      <div className="rp-card-top">
        <span className={`rp-status rp-status--${st}`}>{STATUS_LABEL[st]}</span>
        {you.approval_status === 'Pending' && !d.decided_at && <span className="rp-flag">Waiting on your approval</span>}
        {you.approval_status === 'Approved' && <span className="rp-flag rp-flag--good">You approved</span>}
        {you.approval_status === 'Declined' && <span className="rp-flag rp-flag--bad">You declined</span>}
        {d.outcome_status && <span className="rp-outcome">Outcome: {d.outcome_status}</span>}
      </div>

      <h2 className="rp-card-title">{d.title || 'Untitled decision'}</h2>
      {d.description && <p className="rp-card-desc">{d.description}</p>}

      <dl className="rp-facts">
        <div>
          <dt>Created</dt>
          <dd><Avatar person={d.created_by} size="sm" />{(d.created_by && d.created_by.name) || 'Unknown'}<span className="rp-muted"> · {formatDate(d.created_at)}</span></dd>
        </div>
        {d.decided_at && (
          <div>
            <dt>Decided</dt>
            <dd>{d.decided_by ? <Fragment><Avatar person={d.decided_by} size="sm" />{d.decided_by.name}<span className="rp-muted"> · </span></Fragment> : null}{formatDate(d.decided_at)}</dd>
          </div>
        )}
        {d.deadline_at && (
          <div>
            <dt>Deadline</dt>
            <dd className={st === 'overdue' ? 'rp-overdue' : ''}>{formatDate(d.deadline_at)}{!d.decided_at && <span className="rp-muted"> · {relative(d.deadline_at)}</span>}</dd>
          </div>
        )}
      </dl>

      {(d.expected_outcome || d.actual_outcome || d.outcome_notes) && (
        <div className="rp-outcomes">
          {d.expected_outcome && <div><span>Expected</span><p>{d.expected_outcome}</p></div>}
          {d.actual_outcome && <div><span>What happened</span><p>{d.actual_outcome}</p></div>}
          {d.outcome_notes && <div><span>Notes</span><p>{d.outcome_notes}</p></div>}
        </div>
      )}

      {d.stakeholders && d.stakeholders.length > 0 && (
        <div className="rp-people">
          <div className="rp-people-head">
            <span>Stakeholders</span>
            <span className="rp-progress" aria-label={`${approved} of ${d.stakeholders.length} approved`}>
              <span style={{ width: `${(approved / d.stakeholders.length) * 100}%` }} />
            </span>
            <span className="rp-muted">{approved}/{d.stakeholders.length} approved</span>
          </div>
          <ul>
            {d.stakeholders.map((s, i) => (
              <li key={`${s.id}-${i}`} className={`rp-person rp-person--${(s.status || 'pending').toLowerCase()}`}>
                <Avatar person={s} size="sm" />
                {s.name || 'Unknown'}
                <span className="rp-person-status">{s.status || 'Pending'}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {convos.length > 0 && (
        <details className="rp-convo">
          <summary>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 6h14v9H9l-4 4z" /></svg>
            {convos.length} linked message{convos.length === 1 ? '' : 's'}
          </summary>
          <ul>
            {convos.map((m, i) => (
              <li key={i} className="rp-msg">
                <Avatar person={m.user} />
                <div>
                  <div className="rp-msg-head">
                    <b>{(m.user && m.user.name) || 'Unknown'}</b>
                    {m.channel && m.channel !== 'Unknown' && <span>#{m.channel}</span>}
                    {m.timestamp && <span>{formatDate(Number(m.timestamp), true)}</span>}
                  </div>
                  <p className={m.isDeleted ? 'rp-msg-deleted' : ''}>{m.isDeleted ? 'This message was deleted in Slack.' : slackText(m.text || '', people)}</p>
                  {m.permalink && !m.isDeleted && <a className="rp-link" href={m.permalink} target="_blank" rel="noopener noreferrer">Open in Slack →</a>}
                </div>
              </li>
            ))}
          </ul>
        </details>
      )}
    </li>
  );
};

export default Report;



// WEBPACK FOOTER //
// ./src/components/DecisionReport.js